import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, useLang } from "../lib/router";
import { useI18n } from "../context/I18nContext";
import { topicPath } from "../lib/urls";
import { ChevronRight, ArrowRight, BookOpen, Building2, Landmark, LineChart, Coins, Bitcoin, Hash, Newspaper } from "lucide-react";
import { api } from "../lib/api";
import { NewsCard } from "../components/NewsCard";
import { ProductRecoRail } from "../components/ProductReco";
import { CATEGORY_MAP } from "../lib/categories";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

const TYPE_META = {
  company: { Icon: Building2, color: "hsl(var(--navy))", schema: "Organization", key: "kgTypeCompany" },
  regulator: { Icon: Landmark, color: "hsl(var(--saffron))", schema: "GovernmentOrganization", key: "kgTypeRegulator" },
  index: { Icon: LineChart, color: "hsl(var(--brand))", schema: "Thing", key: "kgTypeIndex" },
  commodity: { Icon: Coins, color: "hsl(var(--gold))", schema: "Thing", key: "kgTypeCommodity" },
  crypto: { Icon: Bitcoin, color: "#F7931A", schema: "Thing", key: "kgTypeCrypto" },
  topic: { Icon: Hash, color: "hsl(var(--bullish))", schema: "Thing", key: "kgTypeTopic" },
};

export default function EntityPage() {
  const { slug } = useParams();
  const lang = useLang();
  const { t } = useI18n();
  const [e, setE] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let on = true;
    setLoading(true); setNotFound(false);
    api.get(`/kg/entity/${slug}?language=${lang}`)
      .then((r) => { if (on) setE(r.data); })
      .catch(() => { if (on) setNotFound(true); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [slug, lang]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" /></div>;
  if (notFound || !e) return (
    <main className="mx-auto max-w-2xl px-4 py-20 text-center">
      <h1 className="font-heading text-2xl font-bold">{t("kgNotFound")}</h1>
      <Link to="/topics" className="mt-4 inline-block text-brand font-semibold">{t("kgBackAll")}</Link>
    </main>
  );

  const meta = TYPE_META[e.type] || TYPE_META.topic;
  const base = origin();
  const prefix = lang === "en" ? "" : `/${lang}`;
  const url = `${base}${prefix}${topicPath(e.slug)}`;
  const catName = CATEGORY_MAP[e.category]?.name || e.category;
  const senti = e.sentiment || {};

  const breadcrumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("kgHome"), item: `${base}${prefix}/` },
      { "@type": "ListItem", position: 2, name: t("kgTopics"), item: `${base}${prefix}/topics` },
      { "@type": "ListItem", position: 3, name: e.name, item: url },
    ],
  };
  const aboutLd = { "@type": meta.schema, name: e.name, description: e.description };
  const collectionLd = {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: `${e.name} — News & Analysis`, url, description: e.description,
    about: aboutLd,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: (e.articles || []).slice(0, 12).map((a, i) => ({
        "@type": "ListItem", position: i + 1, name: a.title,
        url: `${base}${prefix}/news/${a.category}/${a.slug || a.id}`,
      })),
    },
  };
  const definedTermLd = e.glossary ? {
    "@context": "https://schema.org", "@type": "DefinedTerm",
    name: e.glossary.term, alternateName: e.glossary.abbr || undefined,
    description: e.glossary.short, inDefinedTermSet: `${base}/glossary`,
  } : null;

  return (
    <main className="mx-auto max-w-[1180px] px-4 sm:px-6 py-6 sm:py-8" data-testid="entity-page">
      <Helmet>
        <title>{`${e.name} — Latest News, Updates & Analysis | Arth Vani`}</title>
        <meta name="description" content={`${e.description} Read the latest ${e.name} news, explainers and market impact — in ${catName} — on Arth Vani.`} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={`${e.name} — News & Analysis`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Arth Vani" />
        <meta property="og:description" content={e.description} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        <script type="application/ld+json">{JSON.stringify(collectionLd)}</script>
        {definedTermLd && <script type="application/ld+json">{JSON.stringify(definedTermLd)}</script>}
      </Helmet>

      <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-brand">{t("kgHome")}</Link><ChevronRight className="h-3 w-3" />
        <Link to="/topics" className="hover:text-brand">{t("kgTopics")}</Link><ChevronRight className="h-3 w-3" />
        <span className="text-foreground font-medium truncate">{e.name}</span>
      </nav>

      {/* Entity header */}
      <header className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid="entity-header">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full" style={{ background: `${meta.color}1a`, color: meta.color }}>
            <meta.Icon className="h-3.5 w-3.5" />{t(meta.key)}
          </span>
          <Link to={`/?category=${e.category}`} className="text-[11px] font-semibold text-muted-foreground hover:text-brand transition-colors">{t("kgInCat")} {catName}</Link>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="entity-name">{e.name}</h1>
        <p className="mt-2 text-base text-foreground/90 leading-relaxed max-w-3xl">{e.description}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="rounded-lg bg-secondary/60 px-3 py-1.5 text-sm"><b className="font-heading text-brand">{e.count}</b> <span className="text-muted-foreground">{t("kgStories")}</span></span>
          {senti.bullish ? <span className="rounded-lg bg-bullish/10 text-bullish px-3 py-1.5 text-sm font-semibold">{senti.bullish} {t("kgBullish")}</span> : null}
          {senti.bearish ? <span className="rounded-lg bg-bearish/10 text-bearish px-3 py-1.5 text-sm font-semibold">{senti.bearish} {t("kgBearish")}</span> : null}
          {e.glossary && (
            <Link to={`/glossary/${e.glossary.slug}`} data-testid="entity-glossary-link"
              className="rounded-lg bg-brand/10 text-brand px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-brand/20 transition-colors">
              <BookOpen className="h-3.5 w-3.5" />{t("kgWhatIs", { name: e.glossary.abbr || e.glossary.term })}
            </Link>
          )}
        </div>
      </header>

      {/* Related entities — the "graph" */}
      {e.related?.length > 0 && (
        <section className="mt-6" data-testid="entity-related">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground mb-2.5">{t("kgConnected")}</h2>
          <div className="flex flex-wrap gap-2">
            {e.related.map((r) => (
              <Link key={r.slug} to={topicPath(r.slug)} data-testid={`related-entity-${r.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-semibold hover:border-brand/40 hover:text-brand transition-colors">
                {r.name}<span className="text-[11px] text-muted-foreground">{r.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Articles for this entity */}
      <section className="mt-8" data-testid="entity-articles">
        <h2 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2 mb-4">
          <Newspaper className="h-5 w-5 text-brand" />{t("kgLatestOn", { name: e.name })}
        </h2>
        {e.articles?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {e.articles.map((a, i) => <NewsCard key={a.id} article={a} index={i} />)}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-8 text-center rounded-xl border border-dashed border-border">{t("kgNoStories")}</p>
        )}
      </section>

      {/* Content commerce */}
      <div className="mt-8">
        <ProductRecoRail category={e.category} title={t("kgProducts", { cat: catName })} subtitle={t("kgProductsSub")} />
      </div>

      <div className="mt-10 pt-6 border-t border-border text-sm text-muted-foreground flex items-center gap-1.5">
        {t("kgExploreAll")} <Link to="/topics" className="text-brand font-semibold inline-flex items-center gap-0.5">{t("kgGraph")} <ArrowRight className="h-3.5 w-3.5" /></Link>
      </div>
    </main>
  );
}
